import { InputHTMLAttributes, useEffect, useRef } from "react";
import styled from "styled-components";
import { useSchema } from "../ContractContext";
import { FieldValueProps } from "../fields/FieldInput";
import { ContractingParty } from "../model/ContractViewModel";
import { useInputFocus } from "../validation/InputValidationContext";
import InputMask from "react-input-mask";
import { ContractFieldWrapper } from "../fields/ContractFieldInput";

interface MaskedInputFieldProps {
  value: FieldValueProps<string>;
  section: "buyer" | "seller" | "sellerCoOwner" | "buyerCoOwner";
  inputProps?: Omit<InputHTMLAttributes<HTMLInputElement>, "value" | "name">;
}

interface CustomInputMaskProps extends MaskedInputFieldProps {
  name: keyof ContractingParty;
  mask: string;
  required: boolean;
}

export const AdditionalFieldDescription = styled.p`
  margin-top: -2em;
  margin-bottom: 2em;
  font-size: ${({ theme }) => theme.fontStyle.small["font-size"]};
  padding 0 ${({ theme }) => theme.spacing.space8};
  color: ${({ theme }) => theme.nafColor.signature.black};
`;

export function CustomInputMask(props: CustomInputMaskProps) {
  const { value, name, section, mask, required, inputProps } = props;
  const schema = useSchema();
  const placeholder = schema.getFieldPlaceholder(section, name);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const focus = useInputFocus(section, name);
  const id = `${section}.${name}`;
  useEffect(() => {
    if (focus) {
      if (inputRef?.current) inputRef.current.focus();

      focus.consume();
    }
  }, [focus]);
  const { current, onChange, onSave } = value;

  return (
    <InputMask
      inputRef={(input) => (inputRef.current = input)}
      name={name}
      id={id}
      type="tel"
      value={typeof current === "string" ? current : ""}
      placeholder={placeholder as string}
      onChange={(event) =>
        onChange(event.target.value.replace(/\s/g, "") || null)
      }
      onBlur={() => onSave(current ? current.replace(/\s/g, "") : null)}
      required={required}
      mask={mask}
      maskChar={null}
      {...inputProps}
    />
  );
}

export function OrganizationNumberInputField(props: MaskedInputFieldProps) {
  const { section, value } = props;
  const name = "organizationNumber";
  return (
    <CustomInputMask
      name={name}
      value={value}
      section={section}
      mask="999 999 999"
      required={false}
    />
  );
}

export function NationalIdentityNumberInputField(
  props: MaskedInputFieldProps & { labelHint?: string }
) {
  const { section, value, labelHint, inputProps } = props;
  const name = "nationalIdentityNumber";

  return (
    <ContractFieldWrapper
      section={section}
      name={name}
      type="tel"
      disabled={inputProps?.disabled}
      required={false}
      labelHint={labelHint}
    >
      <CustomInputMask
        name={name}
        value={value}
        section={section}
        mask="999999 99999"
        required={false}
        inputProps={inputProps}
      />
    </ContractFieldWrapper>
  );
}
